import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { TID } from "@/lib/testIds";

const HEADLINES = [
  "More than joinery.",
  "The spaces people live in.",
  "The projects builders depend on.",
  "Built to last for decades.",
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85";

// Signature on-load reveal: split a phrase into words, wrap
// each in a fixed-height mask, and slide each word up from
// below with a stagger. This is the "wow" moment.
function MaskedWords({ text, delay = 0 }) {
  const words = text.split(" ");
  return (
    <span className="inline-flex flex-wrap gap-x-[0.28em]">
      {words.map((w, i) => (
        <span
          key={`${w}-${i}`}
          className="inline-block overflow-hidden align-bottom"
          style={{ lineHeight: 0.95 }}
        >
          <motion.span
            className="inline-block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 1.1,
              ease: [0.22, 1, 0.36, 1],
              delay: delay + i * 0.09,
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [firstMount, setFirstMount] = useState(true);
  const { scrollY } = useScroll();
  const imgScale = useTransform(scrollY, [0, 800], [1.08, 1.18]);
  const imgY = useTransform(scrollY, [0, 800], [0, 80]);
  const overlay = useTransform(scrollY, [0, 600], [0.35, 0.7]);

  useEffect(() => {
    // Let the first masked reveal breathe, then start the cycle.
    const kick = setTimeout(() => setFirstMount(false), 2600);
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % HEADLINES.length);
    }, 3600);
    return () => {
      clearInterval(t);
      clearTimeout(kick);
    };
  }, []);

  return (
    <section
      id="top"
      data-testid={TID.hero.root}
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-charcoal text-cream"
    >
      <motion.div
        style={{ scale: imgScale, y: imgY }}
        className="absolute inset-0 will-change-transform"
      >
        {/* Signature reveal: image "curtain" wipes down on load */}
        <motion.img
          src={HERO_IMG}
          alt="Luxury kitchen with stone benchtops and warm timber cabinetry"
          className="h-full w-full object-cover"
          initial={{ scale: 1.25, filter: "blur(14px)", opacity: 0 }}
          animate={{ scale: 1.08, filter: "blur(0px)", opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 bg-gradient-to-b from-charcoal/55 via-charcoal/30 to-charcoal/95"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/40 via-transparent to-transparent" />

      {/* Fine grain overlay for editorial texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="absolute inset-0 flex flex-col justify-between">
        <div className="pt-28 md:pt-32 px-6 md:px-12 flex items-start justify-between">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-cream/70"
          >
            Est. Sydney — Australia · Homeowners · Builders · Developers
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-cream/70 hidden md:block"
          >
            Vol. 01 / The Modern Kitchen
          </motion.span>
        </div>

        <div className="px-6 md:px-12 pb-20 md:pb-28">
          <div className="max-w-5xl">
            {/* Chapter marker — Awwwards-style manifesto framing */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="mb-6 md:mb-8 flex items-center gap-4 text-brass"
            >
              <span className="h-px w-10 bg-brass" />
              <span className="text-[11px] tracking-[0.34em] uppercase">
                Ch. 01 — The Promise
              </span>
            </motion.div>

            <h1
              data-testid={TID.hero.headline}
              className="font-display font-light text-[14vw] leading-[0.95] md:text-[8.5vw] lg:text-[7.2vw] tracking-tightest text-cream"
            >
              {firstMount ? (
                <MaskedWords text={HEADLINES[0]} delay={0.55} />
              ) : (
                <AnimatePresence mode="wait">
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, y: 28, filter: "blur(8px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="block"
                  >
                    {HEADLINES[index]}
                  </motion.span>
                </AnimatePresence>
              )}
            </h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.6 }}
              className="mt-8 flex gap-2"
            >
              {HEADLINES.map((_, i) => (
                <span
                  key={i}
                  className={`h-px transition-all duration-700 ${
                    (firstMount ? 0 : index) === i
                      ? "w-14 bg-brass"
                      : "w-8 bg-cream/30"
                  }`}
                />
              ))}
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 0.8 }}
          data-testid={TID.hero.scrollHint}
          className="absolute bottom-6 right-6 md:bottom-10 md:right-10 text-[11px] tracking-[0.3em] uppercase text-cream/70 flex items-center gap-3"
        >
          <span className="hidden md:inline">Scroll</span>
          <span className="block h-10 w-px bg-cream/40 animate-pulse" />
        </motion.div>
      </div>
    </section>
  );
}
